import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  COLOURS,
  FillInBlanksAnswer,
  SettingsProps,
} from '../fill-in-blanks.constants';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
  SettingSmallText,
} from 'approot/activity-builder/settings/settings.style';
import { MultiViewProps } from 'approot/shared/layout/layout-multi-view';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import { FILL_IN_BLANKS_SENTENCE } from './fill-in-blanks-settings.constants';
import { useFillInBlanksSettingsContext } from './fill-in-blanks-settings.context';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { ColourTokenRadio } from 'approot/shared/colour/colour-token';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { RangeFieldSetting } from '../../shared/range-field';
import Switch from 'approot/shared/switch/switch';
import { TextInputStyled } from 'approot/shared/styles/inputs.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { NegativeButton } from 'approot/shared/styles/buttons';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { PStyled } from 'approot/shared/styles/typography.style';
import { getSuggestedAnswerItemPayload } from '../../shared/logic/get-suggested-answer-item-payload';

const MIN_WIDTH = 100;
const MAX_WIDTH = 1073;

const WIDTH_FIELD = 'width';
const PLACEHOLDER_FIELD = 'placeholderField';
const LABEL_FIELD = 'labelField';

type FormData = {
  [WIDTH_FIELD]: number;
};

const FillInBlanksSettingsAnswerDetail = ({
  id,
  sectionId,
  properties,
  suggestedAnswerProperties,
  goTo,
}: SettingsProps & MultiViewProps) => {
  const { setEditModeTargetElement } = useActivityBuilderContext();
  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();
  const { activeAnswer, activeSentence } = useFillInBlanksSettingsContext();

  const {
    isVisible: isDeleteModalVisible,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useSimpleModalState();

  const sentence = useMemo(
    () =>
      properties.fillInBlanks.sentences.find(
        s => s.itemId === activeSentence.itemId
      ),
    [activeSentence, properties.fillInBlanks.sentences]
  );

  const { answer, answerIndex } = useMemo(() => {
    if (sentence) {
      const index = sentence.answerBlocks.findIndex(
        answer => answer.itemId === activeAnswer.itemId
      );
      return {
        answer: sentence.answerBlocks[index],
        answerIndex: index,
      };
    }
    return {
      answer: undefined,
      answerIndex: 0,
    };
  }, [sentence, activeAnswer]);

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [WIDTH_FIELD]: answer?.width || 100,
    },
  });
  const { reset, watch } = formMethods;
  const widthValue = watch(WIDTH_FIELD);

  const handleUpdateAnswer = useCallback(
    (updatedAnswer: FillInBlanksAnswer) => {
      const updatedSentences = properties.fillInBlanks.sentences.map(
        sentence => {
          return sentence.itemId === activeSentence.itemId
            ? {
                ...sentence,
                answerBlocks: sentence.answerBlocks.map(answer =>
                  answer.itemId === updatedAnswer.itemId
                    ? updatedAnswer
                    : answer
                ),
              }
            : sentence;
        }
      );
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences: updatedSentences,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            sentences: updatedSentences,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [
      id,
      sectionId,
      properties,
      activeSentence.itemId,
      updateComponentProperties,
    ]
  );

  const handleDeleteAnswer = useCallback(
    (sentenceItemId: string, answerItemId: string) => {
      const suggestedAnswers = suggestedAnswerProperties?.suggestedAnswers
        ?.filter(answer => answer.answerReferences?.id !== answerItemId)
        .map(answer => getSuggestedAnswerItemPayload(answer));

      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences: properties.fillInBlanks.sentences.map(sentence => {
                  return sentence.itemId === sentenceItemId
                    ? {
                        ...sentence,
                        answerBlocks: sentence.answerBlocks.filter(
                          answer => answer.itemId !== answerItemId
                        ),
                      }
                    : sentence;
                }),
              },
            },
          },
          suggestedAnswers,
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
        isSaveImmediate: true,
        onCompleted: () => {
          closeDeleteModal();
          goTo(FILL_IN_BLANKS_SENTENCE);
          setEditModeTargetElement(sentenceItemId);
        },
      });
    },
    [
      id,
      sectionId,
      properties,
      suggestedAnswerProperties,
      closeDeleteModal,
      goTo,
      setEditModeTargetElement,
      updateComponentProperties,
    ]
  );

  useEffect(() => {
    reset({
      [WIDTH_FIELD]: answer?.width,
    });
    //eslint-disable-next-line
  }, [answer?.itemId, reset]);

  return (
    <ActivityBuilderDrawerWrapper key={activeAnswer?.itemId}>
      <>
        {isDeleteModalVisible && (
          <ActivityBuilderModal
            isVisible
            title="Delete Blank Space"
            headerTitle="Delete Blank Space"
            actions={
              <NegativeButton
                loading={loading}
                disabled={loading}
                onClick={() =>
                  handleDeleteAnswer(activeSentence.itemId, activeAnswer.itemId)
                }
                data-selector="modal-delete-blank-space"
              >
                Delete Blank Space
              </NegativeButton>
            }
            onClose={closeDeleteModal}
          >
            <PStyled>Are you sure you want to delete this blank space?</PStyled>
          </ActivityBuilderModal>
        )}
      </>
      <ActivityBuilderDrawerHeader
        isBack
        title={`Blank Space ${answerIndex + 1}`}
        onClose={() => {
          goTo(FILL_IN_BLANKS_SENTENCE);
          setEditModeTargetElement(sentence?.itemId);
        }}
        headerButton={
          <DeleteButtonIcon
            $isSmall
            aria-label="Delete this blank space"
            onClick={openDeleteModal}
            data-selector="btn-icon-delete-blank-space"
          />
        }
      />
      <ActivityBuilderDrawerContent>
        {answer && (
          <SettingsGroup as="fieldset" marginBottom={0}>
            <ScreenReaderOnly as="legend">
              Answer field settings
            </ScreenReaderOnly>

            <SettingsGroup>
              <ScreenReaderOnly as="label">
                Blank space width in pixels
              </ScreenReaderOnly>
              <FormProvider {...formMethods}>
                <RangeFieldSetting
                  fieldName={WIDTH_FIELD}
                  label="Input Length"
                  postfix="px"
                  max={MAX_WIDTH}
                  min={MIN_WIDTH}
                  value={widthValue}
                  onChange={width =>
                    handleUpdateAnswer({
                      ...answer,
                      width,
                    })
                  }
                />
              </FormProvider>
            </SettingsGroup>

            <SettingsHr />

            <SettingsGroup>
              <SettingsLabel>Colour</SettingsLabel>
              <div role="radiogroup">
                {COLOURS.map(colour => (
                  <ColourTokenRadio
                    key={colour.colour1}
                    name="colourField"
                    colours={colour}
                    isActive={colour.base === answer.colour}
                    onChange={() =>
                      handleUpdateAnswer({
                        ...answer,
                        colour: colour.base,
                      })
                    }
                  />
                ))}
              </div>
            </SettingsGroup>

            <SettingsHr />

            <SettingsGroup>
              <SettingsLabel>Add Label</SettingsLabel>
              <Switch
                ariaLabel={`${answer.showLabel ? 'Hide' : 'Show'} answer label`}
                state={!!answer.showLabel}
                onClick={() => {
                  handleUpdateAnswer({
                    ...answer,
                    showLabel: !answer.showLabel,
                    label: answer.label || 'Text',
                  });
                }}
              />
            </SettingsGroup>

            {answer.showLabel && (
              <SettingsGroup>
                <SettingsLabel htmlFor={LABEL_FIELD}>Label</SettingsLabel>
                <TextInputStyled
                  id={LABEL_FIELD}
                  name={LABEL_FIELD}
                  defaultValue={answer.label || ''}
                  onChange={event =>
                    handleUpdateAnswer({
                      ...answer,
                      label: event.target.value,
                    })
                  }
                />
              </SettingsGroup>
            )}

            <SettingsHr />

            <SettingsGroup>
              <SettingsLabel htmlFor={PLACEHOLDER_FIELD}>
                Placeholder
              </SettingsLabel>
              <TextInputStyled
                id={PLACEHOLDER_FIELD}
                name={PLACEHOLDER_FIELD}
                defaultValue={answer.placeholder || ''}
                onChange={event =>
                  handleUpdateAnswer({
                    ...answer,
                    placeholder: event.target.value,
                  })
                }
              />
            </SettingsGroup>

            <SettingsGroup>
              <SettingsLabel>Scaffold Placeholder Text</SettingsLabel>
              <Switch
                ariaLabel={`${
                  answer.showPlaceholderAsScaffold ? 'Hide' : 'Show'
                } scaffold placeholder text`}
                state={!!answer.showPlaceholderAsScaffold}
                onClick={() => {
                  handleUpdateAnswer({
                    ...answer,
                    showPlaceholderAsScaffold:
                      !answer.showPlaceholderAsScaffold,
                  });
                }}
              />
            </SettingsGroup>

            <SettingsHr />

            <SettingsGroup>
              <SettingsLabel htmlFor="punctuation">
                Punctuation at the end
              </SettingsLabel>
              <CheckableSingle
                name="punctuation"
                selected={answer.punctuation || 'none'}
                options={[
                  {
                    label: 'None',
                    value: 'none',
                  },
                  {
                    label: '.',
                    value: '.',
                    tooltipText: 'Full stop',
                  },
                  {
                    label: ',',
                    value: ',',
                    tooltipText: 'Comma',
                  },
                  {
                    label: '!',
                    value: '!',
                    tooltipText: 'Exclamation point',
                  },
                  {
                    label: '?',
                    value: '?',
                    tooltipText: 'Question mark',
                  },
                  {
                    label: ':',
                    value: ':',
                    tooltipText: 'Colon',
                  },
                  {
                    label: ';',
                    value: ';',
                    tooltipText: 'Semi-colon',
                  },
                  {
                    label: '“ ”',
                    value: '"',
                    tooltipText: 'Quotation marks',
                  },
                ]}
                onSelect={value => {
                  handleUpdateAnswer({
                    ...answer,
                    punctuation: value === 'none' ? null : value,
                  });
                }}
              />
              <SettingSmallText marginTop={4}>
                This will append punctuation to the end of the blank space.
              </SettingSmallText>
            </SettingsGroup>
          </SettingsGroup>
        )}
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default FillInBlanksSettingsAnswerDetail;
